import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Buildings, GraduationCap, UsersThree, CalendarBlank, PencilSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Nom cliquable : un clic transforme le texte en champ éditable (Entrée pour valider, Échap pour annuler). */
function InlineName({ value, onSave, className }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);

  if (!editing) {
    return (
      <span className={`group inline-flex cursor-text items-center gap-1.5 ${className || ""}`} onClick={() => setEditing(true)} title="Cliquer pour renommer">
        {value}
        <PencilSimple size={11} className="opacity-0 transition-opacity group-hover:opacity-50" />
      </span>
    );
  }
  const commit = () => { setEditing(false); if (val.trim() && val.trim() !== value) onSave(val.trim()); else setVal(value); };
  return (
    <Input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { setVal(value); setEditing(false); }
      }}
      className="h-7 text-sm"
    />
  );
}

/** Petit bouton "+" qui se transforme en champ de saisie pour ajouter un élément à l'intérieur d'un conteneur. */
function InlineAdd({ label, onAdd, testId }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");

  const submit = () => { if (val.trim()) { onAdd(val.trim()); setVal(""); setOpen(false); } };

  if (!open) {
    return (
      <button type="button" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline" onClick={() => setOpen(true)} data-testid={testId}>
        <Plus size={12} /> {label}
      </button>
    );
  }
  return (
    <div className="flex gap-1.5">
      <Input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Nom..."
        className="h-7 text-sm"
        data-testid={`${testId}-input`}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") { setOpen(false); setVal(""); }
        }}
      />
      <Button size="sm" className="h-7 bg-primary px-2 hover:bg-primary/90" onClick={submit} data-testid={`${testId}-confirm`}>OK</Button>
    </div>
  );
}

export default function Academic() {
  const [items, setItems] = useState([]);
  const [newCampus, setNewCampus] = useState("");
  const [newFiliere, setNewFiliere] = useState("");

  const load = () => api.get("/academic").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const childCount = (id) => items.filter((i) => i.parent === id).length;

  const addItem = async (name, type, parent = null) => {
    try { await api.post("/academic", { name, type, parent }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const renameItem = async (item, name) => {
    try { await api.put(`/academic/${item.id}`, { name, type: item.type, parent: item.parent }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (item) => {
    const n = childCount(item.id);
    if (n > 0) {
      toast.error(`Impossible de supprimer : ${n} élément(s) encore rattaché(s). Supprimez-les d'abord.`);
      return;
    }
    try { await api.delete(`/academic/${item.id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const campuses = items.filter((i) => i.type === "campus");
  const filieres = items.filter((i) => i.type === "filiere");
  const promotionsOf = (filiereId) => items.filter((i) => i.type === "promotion" && i.parent === filiereId);
  const semestresOf = (promotionId) => items.filter((i) => i.type === "semestre" && i.parent === promotionId);

  const addCampus = () => { if (newCampus.trim()) { addItem(newCampus.trim(), "campus"); setNewCampus(""); } };
  const addFiliere = () => { if (newFiliere.trim()) { addItem(newFiliere.trim(), "filiere"); setNewFiliere(""); } };

  return (
    <div className="space-y-6" data-testid="academic-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Structure</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Gestion académique</h1>
        <p className="text-sm text-muted-foreground">Une filière contient des promotions, une promotion contient des semestres. Ajoutez-les directement à l'intérieur de leur conteneur ci-dessous. Cliquez sur un nom pour le renommer.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Campus */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Buildings size={20} weight="duotone" /></div>
            <h3 className="font-display font-bold">Campus</h3>
          </div>
          <div className="space-y-2">
            {campuses.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-sm">
                <InlineName value={c.name} onSave={(n) => renameItem(c, n)} />
                <button onClick={() => del(c)}><Trash size={14} className="text-accent" /></button>
              </div>
            ))}
            {campuses.length === 0 && <p className="text-xs text-muted-foreground">Aucun élément</p>}
          </div>
          <div className="mt-3 flex gap-1.5 border-t border-border pt-3">
            <Input value={newCampus} onChange={(e) => setNewCampus(e.target.value)} placeholder="Nouveau campus..." className="h-8 text-sm" onKeyDown={(e) => e.key === "Enter" && addCampus()} data-testid="add-campus-input" />
            <Button size="sm" className="h-8 bg-primary hover:bg-primary/90" onClick={addCampus} data-testid="add-campus-confirm"><Plus size={14} /></Button>
          </div>
        </div>

        {/* Filières → Promotions → Semestres */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><GraduationCap size={20} weight="duotone" /></div>
            <h3 className="font-display font-bold">Filières</h3>
          </div>

          <div className="space-y-4">
            {filieres.map((f) => (
              <div key={f.id} className="rounded-lg border border-border p-3" data-testid={`filiere-card-${f.id}`}>
                <div className="flex items-center justify-between">
                  <InlineName value={f.name} onSave={(n) => renameItem(f, n)} className="font-semibold" />
                  <button onClick={() => del(f)}><Trash size={14} className="text-accent" /></button>
                </div>

                <div className="mt-2 ml-3 space-y-2 border-l-2 border-border pl-3">
                  {promotionsOf(f.id).map((p) => (
                    <div key={p.id}>
                      <div className="flex items-center justify-between text-sm">
                        <InlineName value={p.name} onSave={(n) => renameItem(p, n)} className="flex items-center gap-1.5 font-medium">
                          <UsersThree size={14} className="text-muted-foreground" />
                        </InlineName>
                        <button onClick={() => del(p)}><Trash size={13} className="text-accent" /></button>
                      </div>

                      <div className="mt-1.5 ml-3 space-y-1.5 border-l-2 border-border pl-3">
                        <div className="flex flex-wrap gap-1.5">
                          {semestresOf(p.id).map((s) => (
                            <span key={s.id} className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">
                              <CalendarBlank size={12} />
                              <InlineName value={s.name} onSave={(n) => renameItem(s, n)} />
                              <button onClick={() => del(s)}><Trash size={11} className="text-accent" /></button>
                            </span>
                          ))}
                        </div>
                        <InlineAdd label="Semestre" onAdd={(name) => addItem(name, "semestre", p.id)} testId={`add-semestre-${p.id}`} />
                      </div>
                    </div>
                  ))}
                  <InlineAdd label="Promotion" onAdd={(name) => addItem(name, "promotion", f.id)} testId={`add-promotion-${f.id}`} />
                </div>
              </div>
            ))}
            {filieres.length === 0 && <p className="text-xs text-muted-foreground">Aucune filière. Ajoutez-en une ci-dessous.</p>}
          </div>

          <div className="mt-4 flex gap-1.5 border-t border-border pt-3">
            <Input value={newFiliere} onChange={(e) => setNewFiliere(e.target.value)} placeholder="Nouvelle filière..." className="h-8 text-sm" onKeyDown={(e) => e.key === "Enter" && addFiliere()} data-testid="add-filiere-input" />
            <Button size="sm" className="h-8 bg-primary hover:bg-primary/90" onClick={addFiliere} data-testid="add-filiere-confirm"><Plus size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
